import { axiosDelete, axiosGet, axiosPatch, axiosPost } from '@/api/handlers';
import { useMutation, useQuery } from '@/api/hooks';
import { useInvalidateQuery } from '@/hooks';
import {
  findUpperRecursive,
  genRecursiveOptions,
  getRecursiveArray,
} from '@/utils';
import type { z } from 'zod';
import {
  RevenueNatureBaseModel,
  RevenueNatureFormSchema,
  RevenueNaturesModel,
  type UpdateRevenueNatureBudgetAgendaSortOrderParamsSchema,
  UpdateRevenueNatureParamsSchema,
} from '../models';
import { revenueNatureNameWithCode } from '../utils';

const RevenueNaturesModelElement = RevenueNaturesModel.element;

export const useRevenueNatures = <
  T = Array<z.infer<typeof RevenueNaturesModelElement>>,
>({
  select,
  enabled,
}: {
  select?: (data: z.infer<typeof RevenueNaturesModel>) => T;
  enabled?: boolean;
} = {}) => {
  return useQuery({
    queryKey: ['revenueNatures'],
    queryFn: async () => {
      const revenueNatures = await axiosGet('/api/revenue-natures/');
      return RevenueNaturesModel.parse(revenueNatures);
    },
    select,
    enabled,
  });
};

export const useRevenueNatureRecursive = (id: string) => {
  return useRevenueNatures({
    select: (data) => findUpperRecursive(id, data, []).reverse(),
    enabled: !!id,
  });
};

export const useRevenueNatureOptions = (upperLevel?: number) => {
  const options = useRevenueNatures({
    select: (data) =>
      genRecursiveOptions(
        getRecursiveArray(
          data.flatMap((d) =>
            !d.upperId
              ? [
                  {
                    ...d,
                    name: revenueNatureNameWithCode(d.code, d.level, d.name),
                  },
                ]
              : [],
          ),
          data.flatMap((d) =>
            !upperLevel || d.level <= upperLevel
              ? [
                  {
                    ...d,
                    name: revenueNatureNameWithCode(d.code, d.level, d.name),
                  },
                ]
              : [],
          ),
        ),
        'id',
        'name',
      ),
  });
  return options;
};

type FormValues = z.infer<typeof RevenueNatureFormSchema>;

export const useCreateRevenueNature = () => {
  const { invalidate } = useInvalidateQuery();
  return useMutation({
    mutationFn: async (values: FormValues) => {
      const { upperIds, period, ...parsed } =
        RevenueNatureFormSchema.parse(values);
      const res = await axiosPost('/api/revenue-natures/', {
        ...parsed,
        upperId: upperIds?.at(-1),
        startYear: period?.[0]?.year() ?? null,
        endYear: period?.[1]?.year() ?? null,
      });
      await invalidate(['revenueNatures']);
      return RevenueNatureBaseModel.parse(res);
    },
  });
};

export const useUpdateRevenueNature = () => {
  const { invalidate } = useInvalidateQuery();
  return useMutation({
    mutationFn: async (values: FormValues & { id: string }) => {
      const { id, upperIds, period, ...payload } =
        UpdateRevenueNatureParamsSchema.parse(values);
      const res = await axiosPatch(`/api/revenue-natures/${id}/`, {
        ...payload,
        upperId: upperIds?.at(-1),
        startYear: period?.[0]?.year() ?? null,
        endYear: period?.[1]?.year() ?? null,
      });
      await invalidate(['revenueNatures']);

      return RevenueNatureBaseModel.parse(res);
    },
  });
};

export const useDeleteRevenueNature = () => {
  const { invalidate } = useInvalidateQuery();
  return useMutation({
    mutationFn: async (id: string) => {
      const res = await axiosDelete(`/api/revenue-natures/${id}/`);
      await invalidate(['revenueNatures']);
      return res;
    },
  });
};

export const useChangeBudgetAgendaOrderOfRevenueNatures = () => {
  const { invalidate } = useInvalidateQuery();
  return useMutation({
    mutationFn: async (
      params: z.infer<
        typeof UpdateRevenueNatureBudgetAgendaSortOrderParamsSchema
      >,
    ) => {
      const res = await axiosPost(
        '/api/revenue-natures/update-budget-agenda-sort-order/',
        params,
      );
      await invalidate(['revenueNatures']);
      return res;
    },
  });
};
